import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { motion, useSpring, useTransform } from 'framer-motion';
import {
  Users,
  Activity,
  BookOpen,
  Search,
  Bell,
  Sparkles,
  Upload,
  ShieldCheck,
  Power,
  Cpu,
  Cloud,
  Clock3,
  CheckCircle2,
  AlertTriangle,
  MailPlus,
  UserPlus,
  VideoIcon,
} from 'lucide-react';
import {
  useQuery,
} from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';

const formatNumber = (num) =>
  Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(num);

const StatCard = ({ icon: Icon, label, value, change, data, color }) => {
  const spring = useSpring(0, { stiffness: 90, damping: 18 });

  useEffect(() => {
    spring.set(value);
  }, [value, spring]);
  const animated = useTransform(spring, (v) => formatNumber(v));


  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="glass-panel glow rounded-3xl p-5 flex flex-col gap-4 border border-white/10"
    >
      <div className="flex items-center justify-between">
        <div className="p-3 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10">
          <Icon className="h-6 w-6 text-cyan-200" />
        </div>
        <span
          className={`text-xs px-3 py-1 rounded-full ${change >= 0 ? 'bg-emerald-500/15 text-emerald-200' : 'bg-rose-500/10 text-rose-200'
            }`}
        >
          {change >= 0 ? '+' : ''}
          {change}%
        </span>
      </div>
      <div>
        <p className="text-sm uppercase tracking-[0.2em] text-slate-400">{label}</p>
        <motion.p className="text-2xl font-semibold text-white mt-1" style={{ color }}>
          {animated}
        </motion.p>
      </div>
      <div className="h-14 w-auto">
        <ResponsiveContainer width="100%" aspect={1} height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <Line type="monotone" dataKey="v" stroke={color} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
};

const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-white/5 rounded-xl ${className}`} />
);

const mock = {
  stats: [
    {
      label: 'Total Users',
      value: 48230,
      change: 8.4,
      color: '#22d3ee',
      icon: Users,
      trend: [62, 70, 68, 74, 79, 81, 90],
    },
    {
      label: 'Active Users',
      value: 18420,
      change: 5.1,
      color: '#7c3aed',
      icon: Activity,
      trend: [32, 35, 37, 40, 42, 44, 48],
    },
    {
      label: 'Courses Uploaded',
      value: 2,
      change: 3.6,
      color: '#a3e635',
      icon: BookOpen,
      trend: [9, 12, 10, 14, 16, 15, 18],
    },
    {
      label: 'Total Videos',
      value: 92,
      change: 1.8,
      color: '#f472b6',
      icon: VideoIcon,
      trend: [86, 87, 88, 89, 90, 91, 92],
    },
  ],

  growthData: [
    { day: 'Mon', users: 6200, signups: 1200 },
    { day: 'Tue', users: 7100, signups: 1350 },
    { day: 'Wed', users: 6800, signups: 1420 },
    { day: 'Thu', users: 7400, signups: 1500 },
    { day: 'Fri', users: 7900, signups: 1680 },
    { day: 'Sat', users: 8120, signups: 1720 },
    { day: 'Sun', users: 9050, signups: 1980 },
  ],
  uploadsByCategory: [
    { category: 'C++', uploads: 8 },
    { category: 'Java', uploads: 8 },
    { category: 'Mobile', uploads: 18 },
    { category: 'Data', uploads: 15 },
    { category: 'Cloud', uploads: 11 },
  ],
  roleSplit: [
    { name: 'Students', value: 55, color: '#22d3ee' },
    { name: 'Instructors', value: 6, color: '#7c3aed' },
    { name: 'Admins', value: 2, color: '#a3e635' },
  ],
  activity: [
    { day: 'Mon', active: 42 },
    { day: 'Tue', active: 48 },
    { day: 'Wed', active: 51 },
    { day: 'Thu', active: 55 },
    { day: 'Fri', active: 62 },
    { day: 'Sat', active: 58 },
    { day: 'Sun', active: 64 },
  ],
  recentUsers: [
    { name: 'Eric.Eng', role: 'Admin', location: 'Kitwe', lastActive: '20m ago' },
  ],
  uploads: [
    { title: 'C++ Programming Solutions', author: 'Christopher Lusoke ', size: '620 MB', status: 'Done' },
    { title: 'Java Programming Solutions', author: 'Eric.Eng', size: '1.2 GB', status: 'Live' },
    { title: 'Edge Computing', author: 'Eric Sakala', size: '480 MB', status: 'Queued' },
  ],
  notifications: [
    { type: 'System', message: 'New platform update deployed', time: 'Just now' },
    { type: 'Security', message: '2FA enabled for 12 admins', time: '8m ago' },
    { type: 'Users', message: '24 new approvals pending', time: '14m ago' },
    { type: 'Content', message: '9 courses flagged for review', time: '26m ago' },
  ],
  approvals: [
    { name: 'Mira Johns', role: 'Instructor', action: 'Verify ID' },
    { name: 'Ibrahim Q.', role: 'Student', action: 'Approve access' },
    { name: 'Tala Noor', role: 'Admin', action: 'Grant SSO' },
  ],
  health: [
    { label: 'API Latency', value: '142 ms', status: 'good' },
    { label: 'Uptime', value: '99.99%', status: 'good' },
    { label: 'Queues', value: '2 delayed', status: 'warn' },
  ],
};

const StatusPill = ({ status }) => {
  const map = {
    good: 'bg-emerald-500/15 text-emerald-200',
    warn: 'bg-amber-500/15 text-amber-200',
    bad: 'bg-rose-500/15 text-rose-200',
  };
  return <span className={`text-xs px-2 py-1 rounded-full ${map[status] || map.good}`}>{status}</span>;
};


const Home = () => {
  const { logout } = useAuth();
  const navigator = useNavigate();
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [countUsers, setCountUsers] = useState(null);
  const baseUrl = 'https://api.codedmastery.com';
  //  const baseUrl = "http://localhost:5000";

  const token = localStorage.getItem('token');

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    const timer = setTimeout(() => setLoading(false), 900);
   
    
    return () => {
      clearInterval(t);
      clearTimeout(timer);
    };
    

  }, []);


  const statsWithTrend = useMemo(
    () =>
      mock.stats.map((s) => ({
        ...s,
        data: s.trend.map((v, i) => ({ v, i })),
      })),
    []
  );

  // console.log('Token in useQuery:\n', token,'\n');// Log the token to verify it's being passed correctly
  const { isLoading, error, data: users } = useQuery({
    // { activeUsers, totalUserCount, courseCount }

    queryKey: ['users'],
    fetchOnWindowFocus: false,
    staleTime:1000 *60*10,
    queryFn: async () => {
      const res = await fetch(`${baseUrl}/auth/all-users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const response = await fetch(`${baseUrl}/auth/count-users_courses`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if(res.status === 401 || response.status === 401) {
        logout();
        navigator('/login');
      }
      const responseData = await res.json();
      const countData = await response.json();

      setCountUsers(countData);

      // console.log('user count ==>', responseData.users);

      return responseData;
    }

  });
  // console.log('data length 1 ==>', countUsers);
  if (error) {
    return <div>Error fetching users: {error.message}</div>;

  }
  if (isLoading) {
    return <div>Loading users...</div>;
  }

  const gradient = (
    <div className="absolute inset-0 -z-10">
      <div className="absolute -top-24 -left-24 h-72 w-72 bg-purple-600/30 blur-3xl" />
      <div className="absolute top-10 right-0 h-72 w-72 bg-cyan-500/25 blur-3xl" />
      <div className="absolute bottom-0 left-1/3 h-64 w-64 bg-lime-400/15 blur-3xl" />
    </div>
  );



  return (
    <div className="relative min-h-screen overflow-hidden">
      {gradient}
      <main className="px-6 lg:px-10 py-6 lg:py-10 space-y-6">
        {/* Top bar */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Coded Mastery Tech. Admin</p>
            <h1 className="text-3xl font-semibold text-white flex items-center gap-3">
              <Sparkles className="h-7 w-7 text-cyan-300" />
              Admin Dashboard
            </h1>
            <p className="text-sm text-slate-400">The admin control panel for managing the platform</p>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-white/5 border border-white/10 min-w-[240px]">
              <Search className="h-4 w-4 text-slate-400" />
              <input
                className="bg-transparent border-none outline-none w-full text-sm text-slate-100 placeholder:text-slate-500"
                placeholder="Search users, courses, logs..."
              />
            </div>
            <div className="flex items-center gap-3 px-4 py-2 rounded-2xl glass-panel border border-white/10">
              <Bell className="h-5 w-5 text-cyan-200" />
              <span className="text-xs px-2 py-1 rounded-full bg-rose-500/20 text-rose-100">8</span>
            </div>
            <div className="glass-panel px-4 py-2 rounded-2xl border border-white/10 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-500 to-cyan-400 flex items-center justify-center font-bold text-slate-900">
                AK
              </div>
              <div>
                <p className="text-sm text-white font-semibold">Admin Coded Mastery</p>
                <p className="text-xs text-slate-400">
                  <Clock3 className="inline h-3 w-3 mr-1" />
                  {now.toLocaleString('en-US', {
                    weekday: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                    timeZone: 'Africa/Lusaka',
                  })}
                </p>
              </div>
            </div>
            <button className="px-4 py-2 rounded-2xl bg-gradient-to-r from-cyan-400 to-purple-500 text-slate-900 font-semibold shadow-glow">
              Quick Actions
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-36" />)
            : statsWithTrend.map((stat) => (


              <StatCard
                key={stat.label}
                icon={stat.icon}
                label={stat.label}
                value={
                  stat.label === 'Total Users' ? countUsers !== null ? countUsers.totalUserCount : stat.value
                    :
                    stat.label === 'Active Users' ? countUsers !== null ? countUsers.activeUsers : stat.value
                      :
                      stat.label === 'Total Videos' ? countUsers !== null ? countUsers.lessonsCount : 0
                        :
                        stat.value
                }

                change={stat.change}
                data={stat.data}
                color={stat.color}
              />
            ))}
        </div>

        {/* Charts row 1 */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 glass-panel rounded-3xl p-5 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-300">User Growth (7 days)</p>
              <span className="text-xs px-2 py-1 rounded-full bg-white/5 border border-white/10">Live</span>
            </div>
            {loading ? (
              <Skeleton className="h-64" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={mock.growthData} margin={{ left: -20, right: 10 }}>
                  <defs>
                    <linearGradient id="lineGradient" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.8} />
                      <stop offset="100%" stopColor="#7c3aed" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="day" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip
                    contentStyle={{ background: '#0b1221', border: '1px solid #1f2937', color: '#e2e8f0' }}
                  />
                  <Line type="monotone" dataKey="users" stroke="url(#lineGradient)" strokeWidth={3} dot={false} />
                  <Line type="monotone" dataKey="signups" stroke="#a3e635" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="glass-panel rounded-3xl p-5 border border-white/10">
            <p className="text-sm text-slate-300 mb-4">Course Uploads by Category</p>
            {loading ? (
              <Skeleton className="h-64" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={mock.uploadsByCategory} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis type="number" stroke="#94a3b8" />
                  <YAxis dataKey="category" type="category" stroke="#94a3b8" />
                  <Tooltip
                    contentStyle={{ background: '#0b1221', border: '1px solid #1f2937', color: '#e2e8f0' }}
                  />
                  <Bar dataKey="uploads" radius={[12, 12, 12, 12]} fill="url(#barGradient)">
                    {mock.uploadsByCategory.map((entry, index) => (
                      <Cell
                        key={entry.category}
                        fill={['#22d3ee', '#7c3aed', '#a3e635', '#f472b6', '#38bdf8'][index % 5]}
                      />
                    ))}
                  </Bar>
                  <defs>
                    <linearGradient id="barGradient" x1="0" x2="1" y1="0" y2="0">
                      <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#7c3aed" stopOpacity={0.7} />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Charts row 2 */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="glass-panel rounded-3xl p-5 border border-white/10">
            <p className="text-sm text-slate-300 mb-4">User Roles</p>
            {loading ? (
              <Skeleton className="h-64" />
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie dataKey="value" data={mock.roleSplit} innerRadius={50} outerRadius={90} paddingAngle={4}>
                    {mock.roleSplit.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip
                    contentStyle={{ background: '#0b1221', border: '1px solid #1f2937', color: '#e2e8f0' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="glass-panel rounded-3xl p-5 border border-white/10 xl:col-span-2">
            <p className="text-sm text-slate-300 mb-4">Platform Activity</p>
            {loading ? (
              <Skeleton className="h-64" />
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={mock.activity}>
                  <defs>
                    <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#22d3ee" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="day" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip
                    contentStyle={{ background: '#0b1221', border: '1px solid #1f2937', color: '#e2e8f0' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="active"
                    stroke="#7c3aed"
                    fillOpacity={1}
                    fill="url(#areaGradient)"
                    strokeWidth={3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Middle tables */}
        <div className="grid grid-cols-1 2xl:grid-cols-3 gap-4">
          <div className="glass-panel rounded-3xl p-5 border border-white/10 2xl:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-300">Admin Activity</p>
              <span className="text-xs text-cyan-200">Recent uploads & users</span>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-slate-200">Recent Uploads</p>
                  <Upload className="h-4 w-4 text-cyan-200" />
                </div>
                <div className="space-y-3">
                  {loading
                    ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)
                    : mock.uploads.map((item) => (
                      <div key={item.title} className="flex items-center justify-between text-sm">
                        <div>
                          <p className="text-white font-medium">{item.title}</p>
                          <p className="text-slate-400 text-xs">{item.author}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-400">{item.size}</p>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${item.status === 'Live'
                              ? 'bg-emerald-500/15 text-emerald-200'
                              : item.status === 'Processing'
                                ? 'bg-amber-500/15 text-amber-200'
                                : 'bg-sky-500/15 text-sky-200'
                              }`}
                          >
                            {item.status}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-slate-200">Recent Users</p>
                  <Users className="h-4 w-4 text-cyan-200" />
                </div>
                <div className="space-y-3">
                  {loading
                    ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)
                    : mock.recentUsers.map((user) => (
                      <div key={user.name} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-500 to-cyan-400 flex items-center justify-center text-xs font-semibold text-slate-900">
                            {user.name
                              .split(' ')
                              .map((n) => n[0])
                              .join('')
                              .slice(0, 2)}
                          </div>
                          <div>
                            <p className="text-white font-medium">{user.name}</p>
                            <p className="text-slate-400 text-xs">{user.role} · {user.location}</p>
                          </div>
                        </div>
                        <span className="text-xs text-slate-400">{user.lastActive}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right widgets */}
          <div className="space-y-4">
            <div className="glass-panel rounded-3xl p-5 border border-white/10">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-slate-200">Approvals</p>
                <ShieldCheck className="h-4 w-4 text-cyan-200" />
              </div>
              <div className="space-y-3">
                {loading
                  ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10" />)
                  : mock.approvals.map((item) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div>
                        <p className="text-white font-medium">{item.name}</p>
                        <p className="text-slate-400 text-xs">{item.role}</p>
                      </div>
                      <button className="text-xs px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-100 border border-cyan-400/30">
                        {item.action}
                      </button>
                    </div>
                  ))}
              </div>
            </div>

            <div className="glass-panel rounded-3xl p-5 border border-white/10">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-slate-200">Notifications</p>
                <Bell className="h-4 w-4 text-cyan-200" />
              </div>
              <div className="space-y-3">
                {loading
                  ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)
                  : mock.notifications.map((note) => (
                    <div key={note.message} className="flex items-center justify-between text-sm">
                      <div>
                        <p className="text-white font-medium">{note.type}</p>
                        <p className="text-slate-400 text-xs">{note.message}</p>
                      </div>
                      <span className="text-xs text-slate-400">{note.time}</span>
                    </div>
                  ))}
              </div>
            </div>

            <div className="glass-panel rounded-3xl p-5 border border-white/10">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-slate-200">System Health</p>
                <Power className="h-4 w-4 text-emerald-300" />
              </div>
              <div className="space-y-3">
                {mock.health.map((h) => (
                  <div key={h.label} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      {h.status === 'good' && <CheckCircle2 className="h-4 w-4 text-emerald-300" />}
                      {h.status === 'warn' && <AlertTriangle className="h-4 w-4 text-amber-300" />}
                      {h.status === 'bad' && <AlertTriangle className="h-4 w-4 text-rose-300" />}
                      <p className="text-white">{h.label}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-300">{h.value}</span>
                      <StatusPill status={h.status} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-panel rounded-3xl p-5 border border-white/10">
              <p className="text-sm text-slate-200 mb-3">Quick Actions</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Add Course', icon: BookOpen },
                  { label: 'Add Admin', icon: UserPlus },
                  { label: 'Send Announcement', icon: MailPlus },
                  { label: 'Upload Content', icon: Upload },
                ].map((action) => (
                  <button
                    key={action.label}
                    onClick={() => {
                      if (action.label === 'Upload Content') {
                        navigator('/upload');
                      }
                    }}
                    className="rounded-2xl bg-white/5 border border-white/10 px-3 py-3 flex items-center gap-2 text-sm text-white hover:-translate-y-1 transition-transform duration-200"
                  >
                    <action.icon className="h-4 w-4 text-cyan-200" />
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer meta */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="glass-panel rounded-2xl p-4 border border-white/10 flex items-center gap-3">
            <Cpu className="h-5 w-5 text-cyan-200" />
            <div>
              <p className="text-sm text-white">Model latency</p>
              <p className="text-xs text-slate-400">AI insights ready in ~240ms</p>
            </div>
          </div>
          <div className="glass-panel rounded-2xl p-4 border border-white/10 flex items-center gap-3">
            <Cloud className="h-5 w-5 text-cyan-200" />
            <div>
              <p className="text-sm text-white">Edge cache</p>
              <p className="text-xs text-slate-400">12 PoPs · autoscale active</p>
            </div>
          </div>
          <div className="glass-panel rounded-2xl p-4 border border-white/10 flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-cyan-200" />
            <div>
              <p className="text-sm text-white">Security</p>
              <p className="text-xs text-slate-400">Zero trust · anomaly watch</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Home;
